"use client";

import { useContext, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { locationcontext } from "@/lib/context";
import { VALID_ROOTS } from "@/constants/routes";

// Detail-type sub-routes under a city — event/contest pages (and their
// booking flows) embed the city in the slug purely for SEO/routing, e.g.
// /surat/event/some-show. Landing on one means browsing a specific ITEM, not
// choosing to browse Surat, so it must never change the visitor's selected
// city. Only "city hub" pages (/{city}, /{city}/events, /{city}/{category},
// ...) should. Mirrors the URL shapes built by lib/routes.js's
// eventUrl()/contestUrl()/bookingUrl().
const CITY_DETAIL_SEGMENTS = new Set(["event", "contest"]);
const isCityDetailPath = (segments) => {
  if (segments.length < 3) return false;
  const [, second, third] = segments;
  return (
    CITY_DETAIL_SEGMENTS.has(second.toLowerCase()) ||
    third.toLowerCase() === "events"
  );
};

// Keeps the visitor's selected city in sync with city-hub URLs (e.g.
// browsing straight to /surat or /surat/events always adopts "surat"),
// while leaving it alone on entity detail pages that merely embed a city in
// their slug (see isCityDetailPath above) and on non-city routes
// (VALID_ROOTS, e.g. /artist/x, /venue/x, /login).
export default function CitySync({ serverCity, isCityPage, isHome }) {
  const pathname = usePathname() || "";
  const router = useRouter();
  const { location, updateLocationAndCookie, setLocation, locationClaimedRef } =
    useContext(locationcontext);
  const syncingRef = useRef(null);
  const lastRefreshedCityRef = useRef(null);

  // 1. Popstate listener for immediate back/forward browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const currentLoc = (location || "").toLowerCase().trim();
      const sCity = (serverCity || "").toLowerCase().trim();
      const currentPath = window.location.pathname;
      if (
        (currentPath === "/" || isHome) &&
        currentLoc &&
        sCity &&
        currentLoc !== sCity
      ) {
        if (lastRefreshedCityRef.current !== currentLoc) {
          lastRefreshedCityRef.current = currentLoc;
          router.refresh();
        }
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [location, serverCity, isHome, router]);

  // 2. City synchronization: only sync on home or verified city hub pages
  useEffect(() => {
    // Home page check: when on "/" (or isHome), if the server-rendered city
    // differs from the active location in context, refresh to fetch updated data.
    if (isHome || pathname === "/") {
      const currentLoc = (location || "").toLowerCase().trim();
      const sCity = (serverCity || "").toLowerCase().trim();

      if (currentLoc && sCity && currentLoc !== sCity) {
        if (lastRefreshedCityRef.current === currentLoc) return;
        lastRefreshedCityRef.current = currentLoc;
        router.refresh();
      } else if (currentLoc && sCity && currentLoc === sCity) {
        lastRefreshedCityRef.current = null;
      }
      return;
    }

    // Only adopt serverCity if this page is a confirmed CMS city page
    if (!isCityPage || !serverCity) return;

    const targetCity = serverCity.toLowerCase().trim();
    const currentLocation = (location || "").toLowerCase().trim();

    if (!targetCity || currentLocation === targetCity) return;
    if (syncingRef.current === targetCity) return;
    syncingRef.current = targetCity;

    if (locationClaimedRef) locationClaimedRef.current = true;
    setLocation(targetCity);

    updateLocationAndCookie(targetCity, false, {}, false).finally(() => {
      if (syncingRef.current === targetCity) syncingRef.current = null;
    });
  }, [pathname, location, serverCity, isCityPage, isHome, router, updateLocationAndCookie, setLocation, locationClaimedRef]);

  return null;
}
